<template>
  <div class="about-page">
    <!-- 页面顶部横幅 -->
    <div class="page-banner">
      <div class="container banner-inner">
        <h1 class="banner-title">{{ pname }}</h1>
        <el-breadcrumb separator="/" class="banner-breadcrumb">
          <el-breadcrumb-item :to="{ path: '/' }">{{ $t('m.home') }}</el-breadcrumb-item>
          <el-breadcrumb-item>{{ pname }}</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
    </div>

    <!-- 主体内容 -->
    <div class="container page-content" v-if="!loading">
      <div class="about-wrapper">
        <div class="about-body" v-html="content" v-if="html"></div>
        <div class="empty-content" v-else>
          <i class="el-icon-document-delete"></i>
          <p>{{ $t('m.noContent') }}</p>
        </div>
      </div>
    </div>

    <div class="loading-state" v-else>
      <i class="el-icon-loading"></i>
      <p>{{ $t('m.loading') }}</p>
    </div>
  </div>
</template>

<script>
import { formatRichText } from "@/utils/site";
export default {
  name: 'About',
  data() {
    return {
      html: '',
      pname: '',
      pid: 0,
      loading: false
    }
  },
  computed: {
    // 正文：渲染前先做安全清洗，后台富文本不可信
    content() {
      return formatRichText(this.html)
    }
  },
  watch: {
    $route: {
      handler(newval) {
        if (newval.path === '/about') {
          this.pid = newval.query.pid
          this.pname = newval.query.pname
          this.getInfo()
        }
      },
      deep: true
    }
  },
  created() {
    this.pname = this.$route.query.pname
    this.pid = this.$route.query.pid
    this.getInfo()
  },
  methods: {
    getInfo() {
      this.loading = true
      this.$api.home.getPageDetail({
        id: this.pid
      }).then(res => {
        if (res.data) {
          this.html = res.data.textcontent || ''
        }
      }).catch(err => {
        console.error('About getInfo error:', err)
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.about-page {
  min-width: 1200px;
  background-color: var(--color-bg-light);
}

.page-banner {
  background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
  padding: 50px 0;
  position: relative;
  overflow: hidden;
}

.page-banner::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
}

.banner-inner {
  position: relative;
  z-index: 1;
}

.banner-title {
  font-size: 36px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-inverse);
  margin: 0 0 16px 0;
  letter-spacing: 4px;
}

.banner-breadcrumb {
  font-size: var(--font-size-sm);
}

.banner-breadcrumb :deep(.el-breadcrumb__inner) {
  color: rgba(255, 255, 255, 0.7);
}

.banner-breadcrumb :deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner) {
  color: var(--color-text-inverse);
  font-weight: var(--font-weight-medium);
}

.banner-breadcrumb :deep(.el-breadcrumb__separator) {
  color: rgba(255, 255, 255, 0.5);
}

.page-content {
  padding: 50px 0;
  min-height: 500px;
}

.about-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  background-color: var(--color-bg-white);
  border-radius: var(--radius-lg);
  padding: 50px 60px;
  box-shadow: var(--shadow-sm);
}

.about-body {
  /* 建立 BFC：把后台富文本里的 float 图片收进正文框内。
     否则浮动图不计入父容器高度，会溢出到下方兄弟元素并把它挤窄。 */
  display: flow-root;
  font-size: var(--font-size-base);
  color: var(--color-text-regular);
  line-height: var(--line-height-relaxed);
}

.about-body :deep(p) {
  margin-bottom: 20px;
}

.about-body :deep(img) {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 20px auto;
  border-radius: var(--radius-sm);
}

/* 同段多图并排：后台把多张图放进同一个 <p>（百分比宽度 + 行内布局），
   上面的 img{display:block} 会逐张拆成独立块、变成上下堆叠；
   此处用 flex 还原并排，间距交给后台的 width 控制。
   用 ~（一般兄弟）而非 +，以兼容图间夹 <br>/注释节点的情况。 */
.about-body :deep(p:has(> img ~ img)) {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
}

.about-body :deep(p:has(> img ~ img) > img) {
  margin: 20px 0;
}

.about-body :deep(h1),
.about-body :deep(h2),
.about-body :deep(h3),
.about-body :deep(h4) {
  color: var(--color-text-primary);
  margin: 30px 0 15px;
  font-weight: var(--font-weight-bold);
}

.about-body :deep(h2) {
  font-size: 22px;
  padding-left: 12px;
  border-left: 4px solid var(--color-primary);
}

.about-body :deep(h3) {
  font-size: 18px;
}

.about-body :deep(ul),
.about-body :deep(ol) {
  padding-left: 24px;
  margin-bottom: 20px;
}

.about-body :deep(li) {
  margin-bottom: 8px;
}

.about-body :deep(blockquote) {
  border-left: 4px solid var(--color-primary);
  background-color: var(--color-primary-bg);
  padding: 16px 20px;
  margin: 20px 0;
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
  color: var(--color-text-secondary);
}

.about-body :deep(a) {
  color: var(--color-primary);
  text-decoration: none;
}

.about-body :deep(a:hover) {
  text-decoration: underline;
}

.about-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
}

.about-body :deep(th),
.about-body :deep(td) {
  border: 1px solid var(--color-border);
  padding: 12px 16px;
  text-align: left;
}

.about-body :deep(th) {
  background-color: var(--color-bg-lighter);
  font-weight: var(--font-weight-medium);
}

.loading-state {
  text-align: center;
  padding: 100px 0;
  color: var(--color-text-placeholder);
}

.loading-state i {
  font-size: 48px;
  margin-bottom: 20px;
  display: block;
  color: var(--color-primary);
}

.loading-state p {
  font-size: var(--font-size-md);
  margin: 0;
}

.empty-content {
  text-align: center;
  padding: 60px 0;
  color: var(--color-text-placeholder);
}

.empty-content i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
}

.empty-content p {
  font-size: var(--font-size-base);
  margin: 0;
}
</style>
